import { History as HistoryIcon, Inbox as InboxIcon, Refresh } from "@mui/icons-material";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import colorTheme from "../../assets/colorTheme";
import DtAlert from "../../components/ui/alert/DtAlert";
import { DtDataGrid } from "../../components/ui/dataGrid";
import { EmptyState } from "../../components/ui/emptyState";
import DtTabs from "../../components/ui/tab/DtTabs";
import { usePushbackClientLogs } from "../../hooks/pushbackDashboard/usePushbackClientLogs";
import { usePushbackDetail } from "../../hooks/pushbackDashboard/usePushbackDetail";
import { usePushbackOrderDetails } from "../../hooks/pushbackDashboard/usePushbackOrderDetails";
import { usePushbackReport } from "../../hooks/pushbackDashboard/usePushbackReport";
import { IPushbackEntry } from "../../models/configuration/pushback/IPushbackLoans";
import { clientLogs } from "../../pages/clientData";
import { orderDetailsColumns, pushBackDetailColumns } from "../../pages/pushbackDashboard/pushbackData";
import PushbackReportingDialog from "../../pages/pushbackDashboard/PushbackReportingDialog";
import { RootState } from "../../state/store";

const LogTabs = () => {
    const [tabIndex, setTabIndex] = useState(0);
    const appState = useSelector((state: RootState) => state.appSlice);
    const loanNumber = appState.loanNumber || '';
    const [orderDetailsOrderId, setOrderDetailsOrderId] = useState<string>('');
    const [reportOrderId, setReportOrderId] = useState<string>('');
    const [reportEntryId, setReportEntryId] = useState<string>('');
    const [reportingDialogOpen, setReportingDialogOpen] = useState(false);

    // Hooks
    const { data: orderDetailsData, error: orderDetailsError, loading: orderDetailsIsLoading } = usePushbackOrderDetails(loanNumber);
    const { data: detailData, error: detailError, loading: detailIsLoading, refresh: refreshDetail } = usePushbackDetail(orderDetailsOrderId);
    const { data: logData, error: logError, loading: logIsLoading } = usePushbackClientLogs(loanNumber);
    const { data: reportData, loading: reportIsLoading, refresh: refreshReport } = usePushbackReport(reportOrderId, reportEntryId);

    const handleViewReport = useCallback((item: IPushbackEntry) => {
        setReportOrderId(item.OrderId);
        setReportEntryId(item.EntryId);
        setReportingDialogOpen(true);
    }, []);

    const reportingDialogClose = useCallback(() => {
        setReportingDialogOpen(false);
    }, []);

    // Add error state for order details and logs
    const hasAnyError = !!(orderDetailsError || logError || detailError);

    return (
        <Stack sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Error Alert */}
            {hasAnyError && (
                <DtAlert severity="error">
                    {orderDetailsError && <span>Failed to load order details. </span>}
                    {detailError && <span>Failed to load pushback details. </span>}
                    {logError && <span>Failed to load client logs. </span>}
                    Please try refreshing or check your connection.
                </DtAlert>
            )}

            <DtTabs
                tabs={[
                    {
                        label: `Pushback (${orderDetailsIsLoading ? "..." : orderDetailsData?.OrderDetails?.length ?? 0})`
                    },
                    {
                        label: `Client Logs (${logIsLoading ? "..." : logData?.ClientLogEntries?.length ?? 0})`
                    },
                    {
                        label: "Form Log"
                    }
                ]}
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
            >
                <Box>
                    {orderDetailsIsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : (orderDetailsData?.OrderDetails?.length ?? 0) > 0 ? (
                        <DtDataGrid
                            data={orderDetailsData?.OrderDetails ? [...orderDetailsData.OrderDetails].sort((a, b) => new Date(b.CreatedTimestamp).getTime() - new Date(a.CreatedTimestamp).getTime()) : []}
                            columns={orderDetailsColumns()}
                            tableName="orderDetails"
                            paged
                            responsivePageSize
                            responsivePageConfig={{ heightRatio: 0.6 }}
                            expanderConfig={{
                                expanderTooltip: (expanded, row) => expanded ? "Hide Pushback details" : "Show Pushback details",
                                onRowExpand: (row, rowIndex, expanded) => {
                                    setOrderDetailsOrderId(expanded ? row.OrderId : "");
                                },
                                renderExpandedRow: (row) =>
                                    orderDetailsOrderId === row.OrderId ? (
                                        detailIsLoading ? (
                                            <Box sx={{ display: "flex", justifyContent: "start", alignItems: "center", minHeight: 25 }}>
                                                <CircularProgress size={25} />
                                            </Box>
                                        ) : detailData?.PushbackEntries?.length ? (
                                            <>
                                                <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 2, fontWeight: 500, mb: 1, color: "text.secondary" }}>
                                                    Pushback Details
                                                    <Button
                                                        size="small"
                                                        startIcon={<Refresh />}
                                                        onClick={refreshDetail}
                                                    >
                                                        Refresh
                                                    </Button>
                                                </Typography>
                                                <Box sx={{ background: colorTheme.palette.background.paper }}>
                                                    <DtDataGrid
                                                        data={[...detailData.PushbackEntries].sort(
                                                            (a, b) =>
                                                                new Date(b.CreatedTimestamp).getTime() -
                                                                new Date(a.CreatedTimestamp).getTime()
                                                        )}
                                                        paged
                                                        columns={pushBackDetailColumns()}
                                                        tableName="pushBackDetails"
                                                        actionsConfig={{
                                                            mode: 'dropdown',
                                                            actions: [{
                                                                type: 'view',
                                                                label: "View Report",
                                                                onClick: (row) => handleViewReport(row)
                                                            }]
                                                        }}
                                                    />
                                                </Box>
                                            </>
                                        ) : (
                                            <Typography sx={{ color: "text.secondary", fontWeight: 500 }}>
                                                No pushback details available.
                                            </Typography>
                                        )
                                    ) : null
                            }}
                        />
                    ) : (
                        <EmptyState
                            icon={<InboxIcon sx={{ fontSize: 48, color: "primary.main" }} />}
                            header="No order details available"
                            subheader="Try changing your filters selection."
                            actionLabel="Refresh List"
                        />
                    )}
                </Box>

                <Box>
                    {logIsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : (logData?.ClientLogEntries?.length ?? 0) > 0 ? (
                        <DtDataGrid
                            data={logData?.ClientLogEntries || []}
                            columns={clientLogs()}
                            tableName="logs"
                            paged
                            responsivePageSize
                            responsivePageConfig={{ heightRatio: 0.6 }}
                        />
                    ) : (
                        <EmptyState
                            icon={<HistoryIcon sx={{ fontSize: 48, color: "primary.main" }} />}
                            header="No client logs available"
                            subheader="Try changing your filters selection."
                        />
                    )}
                </Box>

                <Box>
                    <EmptyState
                        icon={<InboxIcon sx={{ fontSize: 48, color: "primary.main" }} />}
                        header="Form Log"
                        subheader="Form log functionality coming soon."
                    />
                </Box>
            </DtTabs>

            <PushbackReportingDialog
                open={reportingDialogOpen}
                closeFxtn={reportingDialogClose}
                reportData={reportData}
                loading={reportIsLoading}
                onRefresh={refreshReport}
            />
        </Stack>
    )
}

export default LogTabs;